import { Injectable } from '@angular/core';
import { Observable, Subject, delay, of, throwError } from 'rxjs';
import { COPY_REJECTION, DELETE_REJECTION, DuplicateCompanyName, RecruitApi, SMART_JD_GONE, SmartJdLocked, SmartJdLockRequired, WRITE_REJECTION } from '../recruit-api';
import { Company, CompanyPage, NewCompany } from '../../models/company.model';
import { Job, JobPage, NewJobRef, RecruitEvent } from '../../models/job.model';
import { NewResume, ResumeUpload } from '../../models/resume.model';
import { SmartJd, SmartJdDocument, SmartJdError, SmartJdLock } from '../../models/smart-jd.model';
import { JobMatches, MatchedCandidate, UnmatchedReason } from '../../models/match-result.model';
import { SAMPLE_FAILING_JD_FILE, SMART_JD_SEED } from './smart-jd.seed';
import { MOCK_CANDIDATES, SAMPLE_UNREADABLE_RESUME_FILE } from './mock-candidates';
import { MockIdentity, MockVerdictBank, scoreCandidate } from './mock-matcher';
import { heldLock } from '../../util/edit-lock';
import { pad2 } from '../../util/text';

type CompanyRecord = Company;

interface CandidateRecord {
  identity: MockIdentity;
  bank: MockVerdictBank;
  /** Fixed in advance for seeded candidates only; an upload is stamped when its score lands. */
  matchedAt?: string;
  /** When the resume was received, which is what the matching list reports. */
  received: string;
  /** Set once the wait has ended with no result. Until then the candidate is still matching. */
  unmatchedReason?: UnmatchedReason;
}

type JobRecord = Job & { companyId: string };

/**
 * A stored Smart JD. `publishedVersion` and the lock fields are deliberately absent: they describe
 * the job rather than one of its documents, and are filled in on the way out.
 */
type StoredSmartJd = Omit<SmartJdDocument, 'publishedVersion' | 'lockedBy' | 'lockExpires'>;

/** Publishing always stamps a version, so a Published document never lacks one. */
type PublishedSmartJd = StoredSmartJd & { version: number };

interface JobSmartJds {
  draft?: StoredSmartJd;
  published?: PublishedSmartJd;
}

/** A candidate's first assessment. Every later one counts up from the match it replaces. */
const FIRST_MATCH = 1;

function minutesAgo(minutes: number): string {
  return new Date(Date.now() - minutes * 60_000).toISOString();
}

/** How long the mock pretends Smart JD generation takes. */
const BUILD_MS = 3000;
/** How long the mock pretends scoring one uploaded resume takes. */
const SCORE_MS = 700;
/** The gap between one publish-triggered re-score landing and the next. */
const RESCORE_MS = 900;
/** The one way a mock build fails, seeded and uploaded alike. */
const PARSE_FAILURE: SmartJdError = { code: 'parse-failed', message: 'The JD could not be read as text.', stage: 'extraction' };
/** The edit lock lasts 15 minutes from the last call, as the backend's does. */
const LOCK_MS = 15 * 60 * 1000;
const SEED_JOB_SHORT_CODE = '1001';
/** A job whose Smart JD has a Draft over a Published v1 — a state no click sequence reaches. */
const DRAFT_OVER_PUBLISHED_SHORT_CODE = '1003';
/** The same, but with the lock held by someone else: the other state no click sequence reaches. */
const LOCKED_JOB_SHORT_CODE = '1004';
/** Seeded mid-build, so the job list has a row to resolve on first load. */
const BUILDING_JOB_SHORT_CODE = '1002';
/** A generation that failed. Nothing a recruiter can do reaches it, and uploading always succeeds. */
const FAILED_JOB_SHORT_CODE = '1005';
/** Mock mode has one identity, so the only other recruiter is a seeded one. */
const OTHER_RECRUITER = 'priya@geektrust.in';
/** Long enough to look at, short enough to watch lapse within a session. */
const SEEDED_LOCK_MS = 10 * 60 * 1000;
const MOCK_PAGE_SIZE = 20;
/** The wire's sentinel for a record whose author it never captured. */
const UNKNOWN_AUTHOR = 'system@gt';
/** The parser authors every Smart JD it generates. */
const PARSER_AUTHOR = 'comala@gt';
/** Stands in for the recruiter who published the seed's Smart JD. */
const MOCK_RECRUITER = 'dhanush@geektrust.in';

function mockDraft(jobShortCode: string, smartJd: SmartJd, created: string): StoredSmartJd {
  return {
    jobShortCode,
    uuid: crypto.randomUUID(),
    status: 'DRAFT',
    version: null,
    updatedBy: PARSER_AUTHOR,
    publishedBy: null,
    publishedAt: null,
    smartJd,
    created,
    updated: created,
  };
}

function mockPublished(jobShortCode: string, smartJd: SmartJd, created: string): PublishedSmartJd {
  return { ...mockDraft(jobShortCode, smartJd, created), status: 'PUBLISHED', version: 1, publishedBy: MOCK_RECRUITER, publishedAt: created };
}

/**
 * A generation that gave up: the document exists and says why, and carries no Smart JD. The
 * contract's status enum has nowhere to put this, so it stays a Draft — the error is what the
 * page branches on, not the status.
 */
function mockFailed(jobShortCode: string, error: SmartJdError, created: string): StoredSmartJd {
  return {
    jobShortCode,
    uuid: crypto.randomUUID(),
    status: 'DRAFT',
    version: null,
    updatedBy: PARSER_AUTHOR,
    publishedBy: null,
    publishedAt: null,
    error,
    created,
    updated: created,
  };
}

/** A Draft and the Published version behind it must never share one object. */
function seedNamed(roleName: string): SmartJd {
  const smartJd = structuredClone(SMART_JD_SEED);
  smartJd.role.name = { ...smartJd.role.name, value: roleName };
  return smartJd;
}

function mockCompany(uuid: string, name: string, url: string, created: string, status: Company['status'] = 'ACTIVE'): Company {
  return { uuid, name, url, companyType: 'Product Startup', assembleCompanyType: 'RECRUIT_AND_INTERVIEW', status, createdBy: UNKNOWN_AUTHOR, created, updated: created };
}

/**
 * In-memory backend for development and demos. State resets on page reload.
 * Timers simulate Smart JD building and resume matching so loading states can be seen.
 */
@Injectable()
export class MockRecruitApi extends RecruitApi {
  private readonly companies: CompanyRecord[] = [
    mockCompany(SMART_JD_SEED.company.id.value, 'MuyCio', 'muycio.com', '2026-09-12T10:00:00.000Z'),
    mockCompany('0b7e4c1a-7a51-4e0f-b1c2-5a3f9d2e8c10', 'Kestrel Health', 'kestrelhealth.in', '2026-09-02T10:00:00.000Z'),
    mockCompany('9d3a2f60-1c4b-4b8e-a0f7-2e6c8b5d4a91', 'Northwind Logistics', 'northwind.co', '2026-08-20T10:00:00.000Z', 'INACTIVE'),
    ...Array.from({ length: 22 }, (_, i) =>
      mockCompany(crypto.randomUUID(), 'Example Company ' + pad2(i + 1), 'example' + (i + 1) + '.com', '2026-08-0' + ((i % 9) + 1) + 'T10:00:00.000Z'),
    ),
  ];
  private readonly jobs: JobRecord[] = [];
  private readonly smartJds = new Map<string, JobSmartJds>();
  /** Keyed on the job, not a document: every write to a job needs the same lock. */
  private readonly locks = new Map<string, SmartJdLock>();
  private readonly candidates = new Map<string, CandidateRecord[]>();
  /** Only candidates whose scoring has come back. Faithful to the wire: the rest are absent. */
  private readonly matches = new Map<string, MatchedCandidate[]>();
  /** Copies taken of each job, so `Copy N` counts as the backend counts it: per source, for ever. */
  private readonly copies = new Map<string, number>();
  /** The seeded building job, until something looks at it and starts its build. */
  private seededBuild: JobRecord | null = null;
  private lastShortCode = 1000;
  /** Stands in for the server-sent stream. It never drops, so it never pretends to reconnect. */
  private readonly events$ = new Subject<RecruitEvent>();

  constructor() {
    super();
    // Still building when a recruiter first opens it, and finishing while they watch. A seeded
    // build that never finished would keep the safety poll running for ever.
    const building: JobRecord = { uuid: crypto.randomUUID(), jobShortCode: BUILDING_JOB_SHORT_CODE, companyId: this.companies[1].uuid, jdFilename: 'Claims backend JD.docx', uploadedBy: UNKNOWN_AUTHOR, status: 'ACTIVE', updatedBy: UNKNOWN_AUTHOR, created: '2026-09-24T08:30:00.000Z', buildStatus: 'BUILDING' };
    this.seededBuild = building;
    this.jobs.push(
      { uuid: crypto.randomUUID(), jobShortCode: SEED_JOB_SHORT_CODE, companyId: this.companies[0].uuid, title: 'Generalist Fullstack', jdFilename: 'Generalist Fullstack JD.pdf', uploadedBy: UNKNOWN_AUTHOR, status: 'ACTIVE', updatedBy: UNKNOWN_AUTHOR, created: '2026-09-12T10:00:00.000Z', buildStatus: 'READY' },
      building,
      // Generation failed, so the parser never named the role either.
      { uuid: crypto.randomUUID(), jobShortCode: FAILED_JOB_SHORT_CODE, companyId: this.companies[1].uuid, jdFilename: 'Underwriting lead JD.pdf', uploadedBy: UNKNOWN_AUTHOR, status: 'ACTIVE', updatedBy: UNKNOWN_AUTHOR, created: '2026-09-26T07:15:00.000Z', buildStatus: 'FAILED' },
    );
    this.smartJds.set(SEED_JOB_SHORT_CODE, { published: mockPublished(SEED_JOB_SHORT_CODE, structuredClone(SMART_JD_SEED), '2026-09-12T10:00:00.000Z') });

    // A recruiter's Draft over a Published v1: resumes are offered on a document the recruiter
    // is not reading, because candidates are still matched against v1.
    this.seedDraftOverPublished(DRAFT_OVER_PUBLISHED_SHORT_CODE, 'Platform Engineer', '2026-09-18T09:00:00.000Z');
    // The lock is seen withholding a Publish that would otherwise be offered, while resumes stay
    // available. Relative expiry, so it lapses during a long session rather than between them.
    this.seedDraftOverPublished(LOCKED_JOB_SHORT_CODE, 'Data Engineer', '2026-09-22T11:00:00.000Z', OTHER_RECRUITER);
    // A failure already on the list when a recruiter arrives. Uploading SAMPLE_FAILING_JD_FILE
    // reaches the same state by clicking, so this seed is for the first screen, not for coverage.
    this.smartJds.set(FAILED_JOB_SHORT_CODE, {
      draft: mockFailed(FAILED_JOB_SHORT_CODE, PARSE_FAILURE, '2026-09-26T07:15:00.000Z'),
    });

    this.candidates.set(
      SEED_JOB_SHORT_CODE,
      MOCK_CANDIDATES.map((m, i) => {
        const at = minutesAgo(m.matchedMinutesAgo);
        return {
          identity: { uuid: i === 0 ? '7442445b-dd34-4653-b364-e35058d309ba' : crypto.randomUUID(), name: m.name, title: m.title, company: m.company, location: m.location },
          bank: m.verdicts,
          matchedAt: at,
          received: at,
        };
      }),
    );
    this.scoreAll(SEED_JOB_SHORT_CODE);
  }

  override listCompanies(page: number): Observable<CompanyPage> {
    const sorted = [...this.companies].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    const start = (Math.max(1, page) - 1) * MOCK_PAGE_SIZE;
    return this.out({
      companies: sorted.slice(start, start + MOCK_PAGE_SIZE).map(({ uuid, name, url, status }) => ({ uuid, name, url, status })),
      totalPages: Math.max(1, Math.ceil(sorted.length / MOCK_PAGE_SIZE)),
      totalCompanies: sorted.length,
    });
  }

  override getCompany(id: string): Observable<Company> {
    const c = this.companies.find((x) => x.uuid === id);
    return c ? this.out(c) : this.notFound('Company');
  }

  override createCompany(input: NewCompany): Observable<Company> {
    if (this.companies.some((c) => c.name.toLowerCase() === input.name.toLowerCase())) {
      return throwError(() => new DuplicateCompanyName());
    }
    const now = new Date().toISOString();
    const rec: CompanyRecord = { ...input, uuid: crypto.randomUUID(), createdBy: UNKNOWN_AUTHOR, created: now, updated: now };
    this.companies.push(rec);
    return this.out(rec, 300);
  }

  override listJobs(companyId: string, page: number): Observable<JobPage> {
    if (this.seededBuild?.companyId === companyId) this.startSeededBuild();
    const all = this.jobs.filter((j) => j.companyId === companyId).sort((a, b) => b.created.localeCompare(a.created));
    const start = (Math.max(1, page) - 1) * MOCK_PAGE_SIZE;
    return this.out({
      jobs: all.slice(start, start + MOCK_PAGE_SIZE).map(({ companyId: _c, ...j }) => j),
      totalPages: Math.max(1, Math.ceil(all.length / MOCK_PAGE_SIZE)),
      totalJobs: all.length,
    });
  }

  override uploadJd(companyId: string, file: File): Observable<NewJobRef> {
    const company = this.companies.find((c) => c.uuid === companyId);
    if (!company) return this.notFound('Company');
    const jobShortCode = this.nextShortCode();
    const created = new Date().toISOString();
    const job: JobRecord = { uuid: crypto.randomUUID(), jobShortCode, companyId, jdFilename: file.name, uploadedBy: UNKNOWN_AUTHOR, status: 'ACTIVE', updatedBy: UNKNOWN_AUTHOR, created, buildStatus: 'BUILDING' };
    this.jobs.push(job);
    this.scheduleBuild(job);
    return this.out({ jobShortCode, buildStatus: 'BUILDING' as const }, 300);
  }

  /**
   * Opens a job from the source's Published Smart JD, published again as version 1 — so nothing
   * is generated and the copy is READY on arrival. The source, its Draft and its lock are left
   * alone, and the clone is deep: two jobs must never share one Smart JD.
   */
  override copyJob(jobShortCode: string): Observable<Job> {
    const source = this.jobs.find((j) => j.jobShortCode === jobShortCode);
    if (!source) return throwError(() => new Error(COPY_REJECTION['job-not-found']));
    const published = this.publishedDoc(jobShortCode);
    if (!published?.smartJd) return throwError(() => new Error(COPY_REJECTION['smart-jd-not-published']));

    const n = (this.copies.get(jobShortCode) ?? 0) + 1;
    this.copies.set(jobShortCode, n);
    const smartJd = structuredClone(published.smartJd);
    // The backend renames the role, and the job's title is that role name.
    const roleName = smartJd.role.name.value + ' Copy ' + n;
    smartJd.role.name = { ...smartJd.role.name, value: roleName };

    const created = new Date().toISOString();
    const copyShortCode = this.nextShortCode();
    const copy: JobRecord = {
      uuid: crypto.randomUUID(), jobShortCode: copyShortCode, companyId: source.companyId, title: roleName,
      jdFilename: source.jdFilename, uploadedBy: MOCK_RECRUITER, status: 'ACTIVE', updatedBy: MOCK_RECRUITER, created, buildStatus: 'READY',
    };
    this.jobs.push(copy);
    // Nothing parsed this one: the recruiter who copied the job is its author as well as its publisher.
    this.smartJds.set(copyShortCode, { published: { ...mockPublished(copyShortCode, smartJd, created), updatedBy: MOCK_RECRUITER } });
    const { companyId: _c, ...job } = copy;
    return this.out(job, 300);
  }

  /**
   * Everything keyed on the short code goes with the job, so every other method here answers the
   * way the backend's 404s will. The record is dropped rather than flagged: nothing can reach it.
   */
  override deleteJob(jobShortCode: string): Observable<void> {
    const at = this.jobs.findIndex((j) => j.jobShortCode === jobShortCode);
    if (at < 0) return throwError(() => new Error(DELETE_REJECTION['job-not-found']));
    this.jobs.splice(at, 1);
    if (this.seededBuild?.jobShortCode === jobShortCode) this.seededBuild = null;
    this.smartJds.delete(jobShortCode);
    this.matches.delete(jobShortCode);
    this.locks.delete(jobShortCode);
    this.copies.delete(jobShortCode);
    return this.out<void>(undefined, 300);
  }

  override getSmartJd(jobShortCode: string): Observable<SmartJdDocument | null> {
    if (this.seededBuild?.jobShortCode === jobShortCode) this.startSeededBuild();
    const doc = this.documentFor(jobShortCode);
    return this.out(doc ? this.toResponse(jobShortCode, doc) : null);
  }


  override uploadResumes(jobShortCode: string, resumes: NewResume[]): Observable<ResumeUpload[]> {
    const job = this.jobs.find((j) => j.jobShortCode === jobShortCode);
    // Resumes are matched against the Published Smart JD, so a job with only a Draft admits none.
    if (!job || !this.publishedDoc(jobShortCode)?.smartJd) {
      return this.out(
        resumes.map((r) => ({ fileName: r.file.name, name: r.name, uploaded: false, error: 'That job has no published Smart JD, so resumes cannot be scored against it.' })),
        300,
      );
    }

    // Uploading only sample files replaces the list, so demos stay tidy. The unreadable one counts
    // as a sample: the walk this exists for is that file alongside the good ones.
    const sampleNames = [...MOCK_CANDIDATES.map((m) => m.fileName), SAMPLE_UNREADABLE_RESUME_FILE];
    const isSampleSet = resumes.length > 0 && resumes.every((r) => sampleNames.includes(r.file.name));
    const existing = isSampleSet ? [] : (this.candidates.get(jobShortCode) ?? []);
    const added: CandidateRecord[] = resumes.map((r, i) => {
      // The unreadable sample has no bank of its own; it never scores, so any stand-in will do.
      const at = sampleNames.indexOf(r.file.name);
      const m = isSampleSet && at < MOCK_CANDIDATES.length ? MOCK_CANDIDATES[at] : MOCK_CANDIDATES[(existing.length + i) % MOCK_CANDIDATES.length];
      return {
        // The recruiter's name wins: it is what the real upload sends and what /matches echoes.
        identity: { uuid: crypto.randomUUID(), name: r.name, title: m.title, company: m.company, location: m.location },
        bank: m.verdicts,
        received: new Date().toISOString(),
      };
    });
    this.candidates.set(jobShortCode, [...existing, ...added]);
    if (isSampleSet) this.matches.set(jobShortCode, []);

    // Until the score lands the candidate is on the matching list, then it moves across and the
    // stream says so — the same two steps the backend takes.
    added.forEach((r, i) => {
      setTimeout(() => {
        // A resume the parser cannot read never reaches scoring, so nothing is ever announced for
        // it: the recruiter learns of it from the next poll alone.
        if (resumes[i].file.name === SAMPLE_UNREADABLE_RESUME_FILE) {
          r.unmatchedReason = 'PARSE_FAILED';
          return;
        }
        const published = this.publishedDoc(jobShortCode);
        // The Smart JD went away between upload and scoring, so no match was ever asked for.
        if (!published?.smartJd) {
          r.unmatchedReason = 'NOT_REQUESTED';
          return;
        }
        const scored = scoreCandidate(r.identity, published.smartJd, r.bank, published, FIRST_MATCH, new Date().toISOString());
        this.matches.set(jobShortCode, [...(this.matches.get(jobShortCode) ?? []), scored]);
        this.announceMatch(jobShortCode, r.identity.uuid);
      }, SCORE_MS * (i + 1));
    });
    return this.out(resumes.map((r) => ({ fileName: r.file.name, name: r.name, uploaded: true, error: null })), 300);
  }

  override listMatches(jobShortCode: string): Observable<JobMatches> {
    const candidates = this.matches.get(jobShortCode) ?? [];
    const scored = new Set(candidates.map((c) => c.candidate_uuid));
    const newestFirst = <T extends { since: string }>(rows: T[]) => rows.sort((a, b) => b.since.localeCompare(a.since));
    const unscored = (this.candidates.get(jobShortCode) ?? []).filter((r) => !scored.has(r.identity.uuid));
    const waiting = (r: CandidateRecord) => ({ candidate_uuid: r.identity.uuid, name: r.identity.name, since: r.received });
    return this.out(
      {
        candidates,
        matching: newestFirst(unscored.filter((r) => !r.unmatchedReason).map(waiting)),
        unmatched: newestFirst(unscored.filter((r) => r.unmatchedReason).map((r) => ({ ...waiting(r), reason: r.unmatchedReason! }))),
      },
      120,
    );
  }

  /**
   * Best-effort, as the hub is: nobody may be listening, and nothing is ever replayed. Always
   * READY, because the mock scorer cannot fail a candidate — the same gap that leaves the
   * could-not-be-scored section unreachable here.
   */
  private announceMatch(jobShortCode: string, candidateUuid: string): void {
    const job = this.jobs.find((j) => j.jobShortCode === jobShortCode);
    if (!job) return;
    this.events$.next({ name: 'match-built', data: { jobShortCode, companyId: job.companyId, candidateUuid, matchStatus: 'READY' } });
  }

  override lockSmartJd(jobShortCode: string): Observable<SmartJdLock> {
    if (!this.documentFor(jobShortCode)) return throwError(() => new Error(SMART_JD_GONE));
    // Standing in for the backend, which keys the lock on the caller's identity.
    const held = this.lockOf(jobShortCode);
    if (held && held.lockedBy !== MOCK_RECRUITER) return throwError(() => new SmartJdLocked(held));
    const lock: SmartJdLock = { lockedBy: MOCK_RECRUITER, lockExpires: new Date(Date.now() + LOCK_MS).toISOString() };
    this.locks.set(jobShortCode, lock);
    return this.out(lock, 120);
  }

  /** Checked against the endpoint's own conflict list, not its happy path: 400 invalid, 404 gone,
   * 409 for no lock, another recruiter's lock, and a Smart JD whose generation failed. */
  override saveSmartJd(jobShortCode: string, smartJd: SmartJd): Observable<SmartJdDocument> {
    const base = this.documentFor(jobShortCode);
    if (!base) return throwError(() => new Error(SMART_JD_GONE));
    if (base.error) return throwError(() => new Error(WRITE_REJECTION['smart-jd-generation-failed']));
    if (!smartJd.company || !smartJd.role) return throwError(() => new Error(WRITE_REJECTION['invalid-smart-jd']));
    const held = this.lockOf(jobShortCode);
    if (!held) return throwError(() => new SmartJdLockRequired());
    if (held.lockedBy !== MOCK_RECRUITER) return throwError(() => new SmartJdLocked(held));
    const jds = this.smartJds.get(jobShortCode);
    const now = new Date().toISOString();
    // A job with only a Published version gets a Draft beside it; the Published one is untouched.
    const draft: StoredSmartJd = {
      ...base,
      uuid: jds?.draft?.uuid ?? crypto.randomUUID(),
      status: 'DRAFT',
      version: null,
      publishedBy: null,
      publishedAt: null,
      updatedBy: MOCK_RECRUITER,
      updated: now,
      smartJd,
    };
    this.smartJds.set(jobShortCode, { ...jds, draft });
    this.locks.set(jobShortCode, { lockedBy: MOCK_RECRUITER, lockExpires: new Date(Date.now() + LOCK_MS).toISOString() });
    return this.out(this.toResponse(jobShortCode, draft), 300);
  }

  override publishSmartJd(jobShortCode: string): Observable<SmartJdDocument> {
    const base = this.documentFor(jobShortCode);
    if (!base) return throwError(() => new Error(SMART_JD_GONE));
    if (base.error) return throwError(() => new Error(WRITE_REJECTION['smart-jd-generation-failed']));
    const held = this.lockOf(jobShortCode);
    if (!held) return throwError(() => new SmartJdLockRequired());
    if (held.lockedBy !== MOCK_RECRUITER) return throwError(() => new SmartJdLocked(held));
    const jds = this.smartJds.get(jobShortCode);
    if (!jds?.draft) return throwError(() => new Error(WRITE_REJECTION['smart-jd-no-draft']));
    const now = new Date().toISOString();
    const published: PublishedSmartJd = {
      ...jds.draft,
      status: 'PUBLISHED',
      version: (jds.published?.version ?? 0) + 1,
      publishedBy: MOCK_RECRUITER,
      publishedAt: now,
      updated: now,
    };
    // The Draft is promoted, not copied: the job is left with one document again, as the backend
    // leaves it — the version this supersedes becomes Unpublished and never reaches the wire.
    this.smartJds.set(jobShortCode, { published });
    this.locks.delete(jobShortCode);
    this.rescore(jobShortCode);
    return this.out(this.toResponse(jobShortCode, published), 300);
  }

  override releaseSmartJdLock(jobShortCode: string): Observable<void> {
    // Releasing a lock the caller does not hold changes nothing — which is what lets the page fire
    // this blind after a failed publish. The mock has to honour that or it lies about the wire.
    if (this.lockOf(jobShortCode)?.lockedBy === MOCK_RECRUITER) this.locks.delete(jobShortCode);
    return this.out<void>(undefined, 80);
  }

  override events(): Observable<RecruitEvent> {
    return this.events$.asObservable();
  }

  /** Generation, as the backend runs it: a while later the job has a Smart JD, or a reason it has none. */
  private scheduleBuild(job: JobRecord, roleName?: string): void {
    const company = this.companies.find((c) => c.uuid === job.companyId);
    if (!company) return;
    setTimeout(() => {
      // The job may have been deleted while this was pending; a deleted job gets no Smart JD and
      // announces nothing, or the delete would half-undo itself.
      if (!this.jobs.includes(job)) return;
      if (job.jdFilename === SAMPLE_FAILING_JD_FILE) {
        this.smartJds.set(job.jobShortCode, { draft: mockFailed(job.jobShortCode, PARSE_FAILURE, job.created) });
        job.buildStatus = 'FAILED';
      } else {
        const smartJd = roleName ? seedNamed(roleName) : structuredClone(SMART_JD_SEED);
        smartJd.company.id = { ...smartJd.company.id, value: company.uuid };
        smartJd.company.name = { ...smartJd.company.name, value: company.name };
        this.smartJds.set(job.jobShortCode, { draft: mockDraft(job.jobShortCode, smartJd, job.created) });
        job.title = smartJd.role.name.value;
        job.buildStatus = 'READY';
      }
      this.events$.next({ name: 'smart-jd-built', data: { jobShortCode: job.jobShortCode, companyId: job.companyId, buildStatus: job.buildStatus } });
    }, BUILD_MS);
  }

  /**
   * The seeded build only starts once someone looks at the job, so it is still building when a
   * recruiter arrives. Starting it at construction would resolve it before anyone navigated.
   */
  private startSeededBuild(): void {
    const job = this.seededBuild;
    if (!job) return;
    this.seededBuild = null;
    this.scheduleBuild(job, 'Claims Backend Engineer');
  }

  /** What `GET` returns: the Draft when there is one, else the Published version. */
  private documentFor(jobShortCode: string): StoredSmartJd | null {
    const jds = this.smartJds.get(jobShortCode);
    return jds?.draft ?? jds?.published ?? null;
  }

  /** The document candidates are scored against: a Draft is never matched. */
  private publishedDoc(jobShortCode: string): PublishedSmartJd | undefined {
    return this.smartJds.get(jobShortCode)?.published;
  }

  private lockOf(jobShortCode: string): SmartJdLock | null {
    const lock = this.locks.get(jobShortCode);
    return lock ? heldLock(lock) : null;
  }

  /**
   * The wire shape. `publishedVersion` is read from the Published sibling even when the Draft is
   * the document being returned — see `decisions/0003-publishedversion-on-the-response`.
   */
  private toResponse(jobShortCode: string, doc: StoredSmartJd): SmartJdDocument {
    const lock = this.lockOf(jobShortCode);
    return {
      ...doc,
      publishedVersion: this.smartJds.get(jobShortCode)?.published?.version ?? null,
      lockedBy: lock?.lockedBy ?? null,
      lockExpires: lock?.lockExpires ?? null,
    };
  }

  private seedDraftOverPublished(jobShortCode: string, roleName: string, created: string, lockedBy?: string): void {
    this.jobs.push({
      uuid: crypto.randomUUID(), jobShortCode, companyId: this.companies[0].uuid, title: roleName,
      jdFilename: roleName + ' JD.pdf', uploadedBy: UNKNOWN_AUTHOR, status: 'ACTIVE', updatedBy: UNKNOWN_AUTHOR, created, buildStatus: 'READY',
    });
    this.smartJds.set(jobShortCode, {
      published: mockPublished(jobShortCode, seedNamed(roleName), created),
      draft: { ...mockDraft(jobShortCode, seedNamed(roleName), created), updatedBy: lockedBy ?? MOCK_RECRUITER },
    });
    if (lockedBy) this.locks.set(jobShortCode, { lockedBy, lockExpires: new Date(Date.now() + SEEDED_LOCK_MS).toISOString() });
  }

  /** Every candidate's first match. Seeding only — an uploaded resume scores on its own timer. */
  private scoreAll(jobShortCode: string): void {
    const published = this.publishedDoc(jobShortCode);
    if (!published?.smartJd) return;
    const recs = this.candidates.get(jobShortCode) ?? [];
    this.matches.set(jobShortCode, recs.map((r) => scoreCandidate(r.identity, published.smartJd!, r.bank, published, FIRST_MATCH, r.matchedAt ?? new Date().toISOString())));
  }

  /**
   * What publishing sets off on the backend: every candidate already scored is scored again against
   * the version just published, its match version counting up. Two are left alone, as the backend
   * leaves them: a candidate still being scored is on the matching list rather than this one, and
   * a candidate whose resume never parsed keeps the version it failed against.
   *
   * The wait is real, because that is the only state `pending` describes: each row carries it from
   * the publish until its own timer fires, and each landing is announced like any other match.
   */
  private rescore(jobShortCode: string): void {
    const published = this.publishedDoc(jobShortCode);
    if (!published?.smartJd) return;
    const smartJd = published.smartJd;
    const byUuid = new Map((this.candidates.get(jobShortCode) ?? []).map((r) => [r.identity.uuid, r]));
    const rows = this.matches.get(jobShortCode) ?? [];
    const dueForRescore = rows.filter((m) => m.status === 'completed' && byUuid.has(m.candidate_uuid));
    const waiting = new Set(dueForRescore.map((m) => m.candidate_uuid));
    this.matches.set(
      jobShortCode,
      rows.map((m) => (waiting.has(m.candidate_uuid) ? { ...m, pending: { match_version: m.match_version + 1, smart_jd_version: published.version } } : m)),
    );

    dueForRescore.forEach((m, i) => {
      setTimeout(() => {
        const current = this.matches.get(jobShortCode) ?? [];
        const row = current.find((r) => r.candidate_uuid === m.candidate_uuid);
        const rec = byUuid.get(m.candidate_uuid);
        // The row may have been deleted, already resolved, or claimed by a later publish, whose
        // version wins: scoring it here would settle a newer wait against an older document.
        if (!rec || row?.pending?.smart_jd_version !== published.version) return;
        const scored = scoreCandidate(rec.identity, smartJd, rec.bank, published, row.pending.match_version, new Date().toISOString());
        this.matches.set(jobShortCode, current.map((r) => (r.candidate_uuid === m.candidate_uuid ? scored : r)));
        this.announceMatch(jobShortCode, m.candidate_uuid);
      }, RESCORE_MS * (i + 1));
    });
  }


  /** Counts past the highest code ever minted: a deleted job's code is never handed out again. */
  private nextShortCode(): string {
    this.lastShortCode = Math.max(this.lastShortCode, ...this.jobs.map((j) => Number(j.jobShortCode) || 0)) + 1;
    return String(this.lastShortCode);
  }

  private out<T>(value: T, ms = 180): Observable<T> {
    return of(structuredClone(value)).pipe(delay(ms));
  }

  private notFound(what: string): Observable<never> {
    return throwError(() => new Error(what + ' not found'));
  }
}
